import type {LessonPlanningCell, LessonPlanningData} from '../types/teacherAllocationAi'
import {buildAllocationSlotKey, getTeacherFullName} from './allocationIds'

/**
 * Pure model behind the Teacher Allocation screens (landing, grid, cell settings, Versi): what a
 * required assignment is, how the filters narrow the matrix, what a removal touches and how an AI
 * proposal changes the counts. Kept free of React so it can be unit tested on its own.
 *
 * Counting rule (handover, Point 1): one required assignment per subject and class/section, not
 * per weekly period. Frequency and consecutive-period edits never change the total; marking a
 * subject as not taught (a deleted slot) or removing a section does.
 */

/** One class/section row of the allocation matrix. */
export type AllocationRow = {
  classKey: string
  /** Stored class name ("Grade 6-A"), as the grid keys it. */
  className: string
  /** Display label (curriculum prefix dropped when unambiguous). */
  label: string
  /** Normalised branch name ("Main" when the class has none). */
  branch: string
  level: string
  curriculum: string
  grade: string
  section: string
  /** Every subject this section studies, including ones marked as not taught. */
  subjects: string[]
}

export type AllocationStatusFilter = 'all' | 'assigned' | 'unassigned'

/** `na`: the class does not study the subject at all (the grid shows an empty cell). */
export type AllocationCellState = 'assigned' | 'unassigned' | 'notTaught' | 'na'

export type AllocationCounts = {total: number; filled: number; remaining: number}

export const ALL_FILTER_VALUE = ''

export const slotKeyFor = (row: Pick<AllocationRow, 'classKey'>, subject: string) =>
  buildAllocationSlotKey(row.classKey, subject)

export const cellTeacher = (grid: LessonPlanningData, classKey: string, subject: string) =>
  String(grid[classKey]?.[subject]?.teacher ?? '').trim()

export function getCellState(
  row: AllocationRow,
  subject: string,
  grid: LessonPlanningData,
  deleted: ReadonlySet<string>
): AllocationCellState {
  if (!row.subjects.includes(subject)) return 'na'
  if (deleted.has(slotKeyFor(row, subject))) return 'notTaught'
  return cellTeacher(grid, row.classKey, subject) ? 'assigned' : 'unassigned'
}

/** Required = studied and not marked as not taught; filled = required with a teacher. */
export function countAllocations(
  rows: AllocationRow[],
  grid: LessonPlanningData,
  deleted: ReadonlySet<string>
): AllocationCounts {
  let total = 0
  let filled = 0
  rows.forEach((row) => {
    row.subjects.forEach((subject) => {
      const state = getCellState(row, subject, grid, deleted)
      if (state === 'assigned' || state === 'unassigned') total += 1
      if (state === 'assigned') filled += 1
    })
  })
  return {total, filled, remaining: total - filled}
}

/**
 * Filter dropdown options (handover, Point 3): with one option that option is shown selected and
 * "All" is omitted; with several, "All" (`''`) comes first and is the default.
 */
export function filterChoices(values: string[]): {values: string[]; hasAll: boolean} {
  const unique = Array.from(new Set(values.filter(Boolean)))
  return {values: unique, hasAll: unique.length > 1}
}

/** The filter value to use: the current one when still offered, else the default by the rule above. */
export function resolveFilterValue(current: string, values: string[]): string {
  const {values: options, hasAll} = filterChoices(values)
  if (options.length === 0) return ALL_FILTER_VALUE
  if (!hasAll) return options[0]
  return current && options.includes(current) ? current : ALL_FILTER_VALUE
}

export function branchValues(rows: AllocationRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.branch).filter(Boolean)))
}

/** Levels offered under the selected branch (all branches when `branch` is All). */
export function levelValues(rows: AllocationRow[], branch: string): string[] {
  return Array.from(
    new Set(
      rows
        .filter((r) => branch === ALL_FILTER_VALUE || r.branch === branch)
        .map((r) => r.level)
        .filter(Boolean)
    )
  )
}

export function rowsInScope(rows: AllocationRow[], branch: string, level: string): AllocationRow[] {
  return rows.filter(
    (r) =>
      (branch === ALL_FILTER_VALUE || r.branch === branch) &&
      (level === ALL_FILTER_VALUE || r.level === level)
  )
}

/** Whether a cell matches the All / Assigned / Unassigned filter (All includes Not taught). */
export function cellMatchesStatus(state: AllocationCellState, filter: AllocationStatusFilter) {
  if (state === 'na') return false
  if (filter === 'all') return true
  if (filter === 'assigned') return state === 'assigned'
  // Not taught has no teacher either: listed under Unassigned (requirement), where it can be
  // reviewed or restored.
  return state === 'unassigned' || state === 'notTaught'
}

export type AllocationMatrix = {
  rows: AllocationRow[]
  columns: string[]
}

/**
 * Rows and columns shown for the current scope and status filter. Alignment is kept: a row or
 * column is hidden only when none of its cells match; nonmatching cells in a shown row are left
 * empty by the caller (`cellMatchesStatus`).
 */
export function buildAllocationMatrix(
  scopedRows: AllocationRow[],
  filter: AllocationStatusFilter,
  grid: LessonPlanningData,
  deleted: ReadonlySet<string>
): AllocationMatrix {
  const columns: string[] = []
  scopedRows.forEach((row) =>
    row.subjects.forEach((subject) => {
      if (!columns.includes(subject)) columns.push(subject)
    })
  )
  if (filter === 'all') return {rows: scopedRows, columns}

  const matches = (row: AllocationRow, subject: string) =>
    cellMatchesStatus(getCellState(row, subject, grid, deleted), filter)
  const rows = scopedRows.filter((row) => columns.some((subject) => matches(row, subject)))
  return {
    rows,
    columns: columns.filter((subject) => rows.some((row) => matches(row, subject))),
  }
}

/** Rows grouped by branch, in first-seen order — the grid shows a header per branch under All. */
export function groupRowsByBranch(
  rows: AllocationRow[]
): Array<{branch: string; rows: AllocationRow[]}> {
  const groups: Array<{branch: string; rows: AllocationRow[]}> = []
  rows.forEach((row) => {
    const group = groups.find((g) => g.branch === row.branch)
    if (group) group.rows.push(row)
    else groups.push({branch: row.branch, rows: [row]})
  })
  return groups
}

/**
 * The sections a subject-column removal affects (handover, 3C "Scope and results"): every row in
 * the Branch/Level scope — not only the rows on screen — whose cell for the subject matches the
 * status filter. Not-taught cells are never removal targets.
 */
export function columnRemovalTargets(
  scopedRows: AllocationRow[],
  subject: string,
  filter: AllocationStatusFilter,
  grid: LessonPlanningData,
  deleted: ReadonlySet<string>
): AllocationRow[] {
  return scopedRows.filter((row) => {
    const state = getCellState(row, subject, grid, deleted)
    if (state !== 'assigned' && state !== 'unassigned') return false
    return cellMatchesStatus(state, filter)
  })
}

// --- Weekly frequency / consecutive periods -----------------------------------------------

export type FrequencyPer = 'week' | 'day'

/** "5/week" -> {count: 5, per: 'week'}; anything unreadable falls back to `fallback`. */
export function parseFrequency(
  value: string | undefined | null,
  fallback = '1/week'
): {count: number; per: FrequencyPer} {
  const read = (raw: string) => {
    const match = /^\s*(\d+)\s*(?:\/\s*(week|day))?\s*$/i.exec(raw)
    if (!match) return null
    const count = Number(match[1])
    if (!Number.isFinite(count) || count < 1) return null
    return {count, per: (match[2]?.toLowerCase() as FrequencyPer) || 'week'}
  }
  return read(String(value ?? '')) ?? read(fallback) ?? {count: 1, per: 'week'}
}

export const formatFrequency = (count: number, per: FrequencyPer = 'week') => `${count}/${per}`

export function parseDuration(value: string | undefined | null, fallback = 1): number {
  const n = Number(String(value ?? '').trim())
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback
}

// --- Pre-fill from the Add Teachers files -------------------------------------------------

export type AllocationHint = {subject?: string; class?: string; section?: string}

const matchKey = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[\s_\-.]+/g, '')
    .trim()

/**
 * Pre-fill from the teacher–subject–class/section relationships found in the Add Teachers files
 * (handover, Point 2). Only complete, unambiguous facts are used:
 * - a hint needs a subject and a class; a section is required when the class has several;
 * - the class/section must identify exactly one row (a name two branches share is ambiguous);
 * - the subject must be one that section studies, still needs a teacher and is not marked as not
 *   taught;
 * - two teachers claiming the same slot leave it unresolved.
 * A teacher's subject alone never fills anything.
 */
export function prefillFromAllocationHints(params: {
  rows: AllocationRow[]
  teachers: any[]
  grid: LessonPlanningData
  deleted: ReadonlySet<string>
  defaultCell: (
    row: AllocationRow,
    subject: string
  ) => Pick<LessonPlanningCell, 'frequency' | 'duration'>
}): {grid: LessonPlanningData; filledKeys: string[]} {
  const {rows, teachers, grid, deleted, defaultCell} = params
  const claims = new Map<string, {row: AllocationRow; subject: string; teacher: any}[]>()

  teachers.forEach((teacher) => {
    const name = getTeacherFullName(teacher)
    const hints: AllocationHint[] = Array.isArray(teacher?.allocationHints)
      ? teacher.allocationHints
      : []
    if (!name || hints.length === 0) return

    hints.forEach((hint) => {
      const subjectKey = matchKey(hint.subject)
      const classKey = matchKey(hint.class)
      if (!subjectKey || !classKey) return
      const sectionKey = matchKey(hint.section)

      const candidates = rows.filter((row) => {
        if (sectionKey) {
          const gradeMatches =
            matchKey(row.grade) === classKey || matchKey(row.className) === classKey
          return gradeMatches && matchKey(row.section) === sectionKey
        }
        // No section: the class name must name the section itself ("Grade 6-A"), or the grade
        // must have only that one section.
        if (matchKey(row.className) === classKey) return true
        if (matchKey(row.grade) !== classKey) return false
        return (
          rows.filter((r) => r.branch === row.branch && matchKey(r.grade) === classKey).length === 1
        )
      })
      if (candidates.length !== 1) return
      const row = candidates[0]
      const subject = row.subjects.find((s) => matchKey(s) === subjectKey)
      if (!subject) return
      const key = slotKeyFor(row, subject)
      const list = claims.get(key) ?? []
      list.push({row, subject, teacher})
      claims.set(key, list)
    })
  })

  const next: LessonPlanningData = {...grid}
  const filledKeys: string[] = []
  claims.forEach((list, key) => {
    const distinct = new Set(list.map((c) => getTeacherFullName(c.teacher)))
    if (distinct.size !== 1) return
    const {row, subject, teacher} = list[0]
    if (deleted.has(key) || cellTeacher(grid, row.classKey, subject)) return
    const existing = grid[row.classKey]?.[subject]
    const defaults = defaultCell(row, subject)
    next[row.classKey] = {
      ...(next[row.classKey] ?? {}),
      [subject]: {
        teacher: getTeacherFullName(teacher),
        teacherEmployeeId: String(teacher?.employeeId ?? '').trim(),
        frequency: existing?.frequency || defaults.frequency,
        duration: existing?.duration || defaults.duration,
      },
    }
    filledKeys.push(key)
  })
  return {grid: next, filledKeys}
}

// --- Versi starting examples ---------------------------------------------------------------

export type AllocationProgressStage = 'zero' | 'partial' | 'full'

export function progressStage(counts: AllocationCounts): AllocationProgressStage {
  if (counts.total > 0 && counts.remaining === 0) return 'full'
  return counts.filled === 0 ? 'zero' : 'partial'
}

/**
 * Facts the starting examples are phrased from — real names from the institution's data. The
 * assignment example names a teacher only from a known teaching relationship (an existing
 * assignment); a teacher name alone does not establish that they can teach a subject.
 */
export type AllocationExampleFacts = {
  /** A known relationship: this teacher already teaches this subject in this grade/branch. */
  knownTeacher: {teacher: string; subject: string; grade: string; branch: string} | null
  /** A subject/section for the frequency and consecutive-period examples. */
  sample: {subject: string; classLabel: string; branch: string; grade: string} | null
}

export function allocationExampleFacts(
  rows: AllocationRow[],
  grid: LessonPlanningData,
  deleted: ReadonlySet<string>
): AllocationExampleFacts {
  let knownTeacher: AllocationExampleFacts['knownTeacher'] = null
  let sample: AllocationExampleFacts['sample'] = null
  for (const row of rows) {
    for (const subject of row.subjects) {
      const state = getCellState(row, subject, grid, deleted)
      if (state === 'notTaught') continue
      if (!sample) {
        sample = {subject, classLabel: row.label, branch: row.branch, grade: row.grade || row.label}
      }
      if (!knownTeacher && state === 'assigned') {
        knownTeacher = {
          teacher: cellTeacher(grid, row.classKey, subject),
          subject,
          grade: row.grade || row.label,
          branch: row.branch,
        }
      }
      if (sample && knownTeacher) return {knownTeacher, sample}
    }
  }
  return {knownTeacher, sample}
}

// --- AI proposals ---------------------------------------------------------------------------

export type ProposalChangeKind =
  | 'new'
  | 'replace'
  | 'unassign'
  | 'frequency'
  | 'duration'
  | 'remove'

/** One row of a proposal preview, with the values before and after. */
export type ProposalChange = {
  slotKey: string
  classKey: string
  subject: string
  kind: ProposalChangeKind
  current: string
  proposed: string
  /** Server flow, frequency changes: classes per week behind a "N/day" value ("1 a day (6/week)"). */
  currentWeekly?: number | null
  proposedWeekly?: number | null
  /**
   * Server flow: the class's own name as the proposal gives it — the label for a class outside
   * the loaded grid (another level or branch), which the grid's rows cannot name.
   */
  className?: string
}

/**
 * The assignment count after a proposal is applied (handover, 4B "Counts"): new assignments add
 * one, replacements and settings edits add nothing, clearing a teacher subtracts one, and a
 * removal takes the requirement out of the total (and out of the filled count when it had a
 * teacher).
 */
export function projectCounts(
  counts: AllocationCounts,
  changes: ProposalChange[]
): AllocationCounts {
  let {total, filled} = counts
  changes.forEach((change) => {
    if (change.kind === 'new') filled += 1
    else if (change.kind === 'unassign') filled -= 1
    else if (change.kind === 'remove') {
      total -= 1
      if (change.current) filled -= 1
    }
  })
  filled = Math.max(0, Math.min(filled, total))
  return {total, filled, remaining: total - filled}
}

/**
 * A proposal that only changes weekly frequency or consecutive periods: no teacher is assigned,
 * cleared or removed, so "X of Y assignments filled" says nothing about it.
 */
export function isSettingsOnlyProposal(changes: ProposalChange[]): boolean {
  return changes.length > 0 && changes.every((c) => c.kind === 'frequency' || c.kind === 'duration')
}

export function countProposalKinds(changes: ProposalChange[]): Record<ProposalChangeKind, number> {
  const out: Record<ProposalChangeKind, number> = {
    new: 0,
    replace: 0,
    unassign: 0,
    frequency: 0,
    duration: 0,
    remove: 0,
  }
  changes.forEach((c) => {
    out[c.kind] += 1
  })
  return out
}

/**
 * Whether any slot a proposal touches has changed since the proposal was made (handover, 4C
 * "Applying and revising"): the saved teacher, settings or not-taught state differ from the
 * snapshot taken with the proposal.
 */
export function snapshotProposalSlots(
  slotKeys: string[],
  grid: LessonPlanningData,
  deleted: ReadonlySet<string>
): Record<string, string> {
  const out: Record<string, string> = {}
  slotKeys.forEach((key) => {
    const idx = key.lastIndexOf('|||')
    const classKey = key.slice(0, idx)
    const subject = key.slice(idx + 3)
    const cell = grid[classKey]?.[subject]
    out[key] = JSON.stringify([
      String(cell?.teacher ?? '').trim(),
      String(cell?.teacherEmployeeId ?? '').trim(),
      String(cell?.frequency ?? ''),
      String(cell?.duration ?? ''),
      deleted.has(key),
    ])
  })
  return out
}

export function proposalIsStale(
  snapshot: Record<string, string>,
  grid: LessonPlanningData,
  deleted: ReadonlySet<string>
): boolean {
  const keys = Object.keys(snapshot)
  const now = snapshotProposalSlots(keys, grid, deleted)
  return keys.some((key) => now[key] !== snapshot[key])
}
